/**
 * The till's offline store (08-clients.md "State"): a Dexie (IndexedDB) database with
 *
 * - `queue`     the command queue: every write the till made that the server has not
 *               confirmed, in the order it was made, each with its Idempotency-Key and
 *               the client ids it carries (order, line, payment and invoice ids);
 * - `orders`    the till's view of each order it has touched while commands wait for the
 *               server (the optimistic projection) next to the last server copy;
 * - `kv`        cached reads (bootstrap bundle, menu, table map, board, order details,
 *               who is signed in) so the POS keeps working without the cloud;
 * - `blocks`    invoice number blocks reserved for this terminal (accounting);
 * - `kotRanges` KOT number ranges reserved for this terminal (kitchen);
 * - `pins`      the staff PIN verifiers for offline sign-in, wrapped on this device.
 *
 * Money in here is the API's JSON form (integer paise as numbers): what the generated
 * client returns, so cached answers and live ones look the same to the screens.
 */
import Dexie, { type Table } from 'dexie';
import type { OrderView } from '@qrkey/api-client';

/**
 * - `queued`    waiting to be sent (or sent and not answered yet).
 * - `conflict`  the server refused it because the order moved on or the offline action
 *               contradicts the server (needs a person: see ConflictsDialog).
 * - `rejected`  the server refused it for good (rules, permission, validation).
 */
export type QueueStatus = 'queued' | 'conflict' | 'rejected';

export interface QueuedCommand {
  seq?: number;
  idempotencyKey: string;
  /** A kind of `syncPayloadSchemas` (packages/contracts/src/sync.ts). */
  kind: string;
  outletId: string;
  terminalId: string;
  /** The order it is about (commands on one order are applied in order, or not at all). */
  orderId: string | null;
  /** The command's payload in the sync API's JSON form. */
  payload: Record<string, unknown>;
  clientAt: string;
  /** The server version of the order the till last saw, on the first command on it. */
  baseVersion: number | null;
  /** Who did it (the offline user), for the list of problems. */
  by: { userId: string | null; name: string | null };
  /** A line for the people who read the list of problems ("Add 2 × Paneer Tikka"). */
  summary: string;
  status: QueueStatus;
  attempts: number;
  createdAt: number;
  /** Do not send before this time (backoff after a retry result). */
  notBefore: number;
  code: string | null;
  detail: string | null;
}

export interface LocalOrder {
  id: string;
  outletId: string;
  /** What the till shows: the server copy with the queued commands applied. */
  view: OrderView;
  /** The last copy the server sent (null: the order was made offline). */
  server: OrderView | null;
  /** Queued commands waiting on this order. */
  pending: number;
  updatedAt: number;
}

export interface KvRow {
  key: string;
  value: unknown;
  at: number;
}

/** A run of consecutive invoice serials of the outlet's series, this terminal's to use. */
export interface LocalBlock {
  id: string;
  outletId: string;
  terminalId: string;
  seriesId: string;
  fy: string;
  prefix: string;
  fromNo: number;
  toNo: number;
  /** Serials already taken by offline bills (a number is never reused). */
  used: number[];
  closed: boolean;
  reservedAt: string;
}

export interface LocalKotRange {
  id: string;
  outletId: string;
  terminalId: string;
  businessDate: string;
  fromNo: number;
  toNo: number;
  used: number[];
}

export interface PinCache {
  /** outletId */
  outletId: string;
  terminalId: string;
  generatedAt: string;
  /** AES-GCM sealed JSON of the verifiers, keyed by a secret that stays on this device. */
  iv: string;
  sealed: string;
  /** Names and roles are not secret: the roster shows them. */
  roster: { userId: string; name: string; roleName: string; canPin: boolean }[];
}

export class OfflineDb extends Dexie {
  queue!: Table<QueuedCommand, number>;
  orders!: Table<LocalOrder, string>;
  kv!: Table<KvRow, string>;
  blocks!: Table<LocalBlock, string>;
  kotRanges!: Table<LocalKotRange, string>;
  pins!: Table<PinCache, string>;

  constructor(name = 'qrkey-staff-offline') {
    super(name);
    this.version(1).stores({
      queue: '++seq, &idempotencyKey, orderId, status, outletId',
      orders: 'id, outletId, pending',
      kv: 'key',
      blocks: 'id, outletId, terminalId',
      kotRanges: 'id, outletId, terminalId, businessDate',
      pins: 'outletId',
    });
  }
}

let current: OfflineDb | null = null;

/** The device's store. Tests replace it (`setDb`) with one over fake-indexeddb. */
export function db(): OfflineDb {
  current ??= new OfflineDb();
  return current;
}

export function setDb(next: OfflineDb | null): void {
  current = next;
}

// --- small key/value helpers --------------------------------------------------

export async function kvGet<T>(key: string): Promise<T | undefined> {
  try {
    return (await db().kv.get(key))?.value as T | undefined;
  } catch {
    return undefined;
  }
}

export async function kvSet(key: string, value: unknown): Promise<void> {
  try {
    await db().kv.put({ key, value, at: Date.now() });
  } catch {
    // Storage full or unavailable: the screen still has the live answer.
  }
}
